import { setShowRateLimitBanner } from '@/store/reducers/rateLimitSlice';
import { setShowServiceUnavailableBanner } from '@/store/reducers/serviceStatusSlice';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError, FetchBaseQueryMeta } from '@reduxjs/toolkit/query';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { setShouldLogout } from '../store/reducers/authSlice';

interface ErrorData {
  message: string;
  errorCode: string;
}

let rateLimitTimer: NodeJS.Timeout | null = null;
let serviceUnavailableTimer: NodeJS.Timeout | null = null;

const baseQuery = fetchBaseQuery({ baseUrl: '/api' });
export const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  Record<any, any>,
  FetchBaseQueryMeta
> = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);
  const errorData = result?.error?.data as ErrorData;
  const isJWTValidationError = errorData?.message?.match(/^Unable to validate JWT/);
  const isInviteTokenValidationError = errorData?.message?.match(/^Unable to validate invite token/);
  if (result.error && result.error.status === 401 && isJWTValidationError) {
    console.debug('Authentication errors, need to refresh token');
    const refreshResult = await baseQuery({ url: '/auth/jwt-refresh', method: 'POST' }, api, extraOptions);

    if (refreshResult?.meta?.response?.status !== 204) {
      console.warn('Token refresh failed, logging out');
      // If not a successful refresh response, log out.
      api.dispatch(setShouldLogout(true));
      return result;
    }

    // The Intercom JWT is self-contained and independently expiring, so it is not invalidated here:
    // the original request below is retried after refresh, and an intercom-jwt request that 401'd
    // recovers on its own. Coupling it to every token refresh caused unnecessary refetch amplification.

    // retry the query
    result = await baseQuery(args, api, extraOptions);
  }

  if (isInviteTokenValidationError) {
    console.debug('Invite token error, logging out');
    api.dispatch(setShouldLogout(true));
  }

  if (result.error?.status === 429) {
    if (rateLimitTimer) {
      clearTimeout(rateLimitTimer);
      rateLimitTimer = null;
    }

    api.dispatch(setShowRateLimitBanner(true));
    rateLimitTimer = setTimeout(() => {
      api.dispatch(setShowRateLimitBanner(false));
      rateLimitTimer = null;
    }, 10_000);
  }

  if (result.error?.status === 503) {
    if (serviceUnavailableTimer) {
      clearTimeout(serviceUnavailableTimer);
      serviceUnavailableTimer = null;
    }

    api.dispatch(setShowServiceUnavailableBanner(true));
    serviceUnavailableTimer = setTimeout(() => {
      api.dispatch(setShowServiceUnavailableBanner(false));
      serviceUnavailableTimer = null;
    }, 30_000); // Extended from 15s — pool exhaustion can take longer to recover
  }

  return result;
};

/**
 * Create a base API to inject endpoints into elsewhere.
 * Components using this API should import from the injected site,
 * in order to get the appropriate types,
 * and to ensure that the file injecting the endpoints is loaded
 */
export const api = createApi({
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'AccountctlRuns',
    'AccountctlTestUsers',
    'Accounts',
    'AccountAdders',
    'AccountMilestones',
    'AccountPayoutEvents',
    'AccountProgress',
    'AccountStipulations',
    'AdminUtilities',
    'Applications',
    'ApprovedVendors',
    'ApprovedVendorListAdmin',
    'ChangeOrders',
    'ConstructionAdders',
    'Contracts',
    'ContractExportStatus',
    'ContractTemplates',
    'ContractTemplateInfo',
    'ContractSigning',
    'CreditReportDetails',
    'CreditReportSummaries',
    'DocumentDocument',
    'EnergyCommunity',
    'EquipmentBrands',
    'EquipmentManufacturers',
    'FinancialAccountDocument',
    'FinancingData',
    'Funds',
    'FundAccounts',
    'History',
    'Incentives',
    'Holdbacks',
    'OrganizationHoldbacks',
    'OrganizationEnergyCommunity',
    'OrganizationPpwModifiers',
    'OrganizationDomesticContent',
    'OrganizationSiteSurveyPricing',
    'OrganizationPropertyTypePricing',
    'OrganizationPaymentSettings',
    'OrganizationPaymentAccounts',
    'OrganizationPaymentPlan',
    'OrganizationPaymentPlanHistory',
    'OrganizationPriceSheetMappings',
    'OrganizationProgress',
    'OrganizationSplitPaySetting',
    'OrganizationDirectPaySetting',
    'OrganizationClawbackSettings',
    'OrganizationUnderwritingSettings',
    'OrganizationReviewSla',
    'OrganizationActivity',
    'OrganizationActivityEvent',
    'OrganizationHoldbackPaymentBatchedSettings',
    'OrganizationHoldbackPayoutEventSettings',
    'OrganizationPausedSettings',
    'OrganizationStatus',
    'Modifiers',
    'Organizations',
    'OrganizationWebhooks',
    'OrganizationWebhooksSubscriptions',
    'OrganizationWebhookLogs',
    'OrganizationWebhookStatistics',
    'OrganizationsByUser',
    'Payees',
    'PaymentPlans',
    'PaymentPlansOrgs',
    'Pricing',
    'ProductLines',
    'Promos',
    'Projects',
    'PayoutTransactions',
    'ProjectTransactionsSummary',
    'PriceSheetMappings',
    'PriceSheetMappingBulkJob',
    'PriceSheets',
    'PropertyReports',
    'PpwModifiers',
    'QueueTasks',
    'Quotes',
    'Queues',
    'SalesReps',
    'SeasonalPricingPlans',
    'SystemDesign',
    'Tasks',
    'Users',
    'Utilities',
    'Batches',
    'ChecklistTemplates',
    'Checklists',
    'RequirementFlagSettings',
    'Warranties',
    'AgingGroup',
    'AccountSummaryExport',
    'WarrantyContractSkus',
    'WarrantyProviders',
    'OrganizationPendingPaymentPlanChanges',
    'ModelOutputs',
    'OrganizationEquipmentSettings',
    'OrganizationEmailSettings',
    'AllocationHistory',
    'DocumentPullReports',
    'MonitoringAccess',
    'OrganizationCoverageAreaSettings',
    'LeadManagement',
    'Leads',
    'IntercomUserJwt',
    'WelcomeCallStipRules',
    'OrganizationMaintenanceSettings',
    'SystemLossThresholds',
  ],
  // This api has endpoints injected in adjacent files.
  endpoints: () => ({}),
});
